import { ArrowUp, Loader2 } from 'lucide-react'
import { Button } from '../ui/shadcn/button'
import { Textarea } from '../ui/shadcn/textarea'
import { MarkdownContent } from '../ui/markdown'
import { useChat } from '@ai-sdk/react'
import type {
  CreatePostScheduleMessage,
  CreatePostScheduleTools,
} from '@backend/routes/stream/createPostSchedule'
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
  type UIMessage,
} from 'ai'
import env from '@/config/env'
import { getApiHeaders } from '@/config/trpc'
import { type Campaign } from '@/pages/schedule-agent'

type ChatProps = {
  inputValue: string
  setInputValue: (value: string) => void
  index: Campaign['index']
  chatHistory: Campaign['chatHistory']
  onAnswerTool: (answer: CreatePostScheduleTools['answer']['output']) => void
  onFinish: (chatHistory: CreatePostScheduleMessage[]) => void
  disabled: boolean
}

export default function Chat({
  inputValue,
  setInputValue,
  index,
  chatHistory,
  onAnswerTool,
  onFinish,
  disabled,
}: ChatProps) {
  const { messages, sendMessage, status, addToolOutput } = useChat<CreatePostScheduleMessage>({
    transport: new DefaultChatTransport({
      api: `${env.VITE_BACKEND_URL}/stream/createPostSchedule`,
      headers: getApiHeaders,
    }),
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,

    // @ts-expect-error AI SDK v6 type depth limit - see https://ai-sdk.dev/docs/troubleshooting/typescript-performance-zod
    messages: chatHistory as CreatePostScheduleMessage[],

    onToolCall: async ({ toolCall }) => {
      if (toolCall.dynamic) {
        return
      }

      if (toolCall.toolName === 'answer') {
        addToolOutput({
          tool: 'answer',
          toolCallId: toolCall.toolCallId,
          output: toolCall.input,
        })

        onAnswerTool(toolCall.input)
      }
    },

    onFinish: (options) => {
      console.log('onFinish', options)
      onFinish(options.messages)
    },
  })

  const kickOffAgent = async (index: Campaign['index'], postFrequency: 'high' | 'low' = 'low') => {
    let firstMessage = 'Please start creating my social media campaign!'

    const v = inputValue.trim()
    if (v) {
      firstMessage += '\n\nSome additional information:\n\n' + v
    }

    sendMessage(
      { text: firstMessage },
      {
        body: {
          postFrequency,
          index: JSON.stringify(
            index.map(({ file, content, classification }) => ({
              file: file.file.name,
              content,
              classification,
            })),
            null,
            2,
          ),
        },
      },
    )
    setInputValue('')
  }

  const handleSendMessage = () => {
    const v = inputValue.trim()
    if (v) {
      sendMessage({ text: v })
      setInputValue('')
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 bg-[#1f1f1f] p-4">
      <div className="flex-1 overflow-y-scroll p-4">
        <div className="">
          {messages.length === 0 ? (
            <div className="text-center text-white/60">
              <p className="text-[14px]"> Start a conversation...</p>
            </div>
          ) : (
            messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                isStreaming={status === 'streaming'}
              />
            ))
          )}
        </div>
      </div>

      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={() => {
          if (messages.length === 0) {
            kickOffAgent(index)
          } else {
            handleSendMessage()
          }
        }}
        disabled={
          disabled || inputValue.trim() === '' || status === 'streaming' || status === 'submitted'
        }
      />
    </div>
  )
}

type ChatInputProps = {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  disabled: boolean
  placeholder?: string
}

const ChatInput = ({ value, onChange, onSend, disabled, placeholder }: ChatInputProps) => {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg bg-[#282828] p-2">
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.shiftKey) return
          if (e.key === 'Enter') {
            e.preventDefault()
            if (!disabled) onSend()
          }
        }}
        placeholder={placeholder || 'Type your message here...'}
        className="outline-hidden focus:outline-hidden w-full resize-none border-none bg-transparent text-sm text-white placeholder-white/60 focus:ring-0"
      />
      <Button onClick={onSend} className="h-10 w-10 rounded-full" disabled={disabled}>
        <ArrowUp size={18} className="text-white" />
      </Button>
    </div>
  )
}

const MessageItem = ({
  message,
  isStreaming = false,
}: {
  message: UIMessage
  isStreaming?: boolean
}) => {
  const renderPart = (part: UIMessage['parts'][number]) => {
    switch (part.type) {
      case 'text':
        return message.role === 'assistant' ? (
          <MarkdownContent isStreaming={isStreaming}>{part.text}</MarkdownContent>
        ) : (
          part.text
        )

      case 'tool-answer': {
        const callId = part.toolCallId

        switch (part.state) {
          case 'input-streaming':
            return (
              <div key={callId}>
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )
          case 'input-available':
            return <pre key={callId}>{JSON.stringify(part.input, null, 2)}</pre>
          case 'output-available':
            return <div key={callId}>Done</div>
          case 'output-error':
            return <div key={callId}>Error: {part.errorText}</div>
        }
      }
    }
    return null
  }

  return (
    <div className={`mb-4 flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className="flex flex-col gap-2">
        {message.parts.map((part, i) => {
          return (
            <div
              key={`${message.id}-${part.type}-${i}`}
              className={`max-w-[350px] rounded-lg p-3 text-white ${message.role === 'user' ? 'bg-[#6864ff]' : 'bg-[#2e2689]'}`}>
              <div className="text-xs text-white/50">{part.type}</div>
              <div className="text-sm">{renderPart(part)}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
